<template>
  <div class="gam-rating-offline">
    <GamSimpleInput v-bind="getRatingInput()" :model-value="ratings.rating" @update:model-value="onLocalRatingChange" />

    <button v-if="isAuth && !showChoices" type="button" class="dont-know-cta" @click="showChoices = true">
      {{ localize('app.component.ratings.item.offline.dont_know') }}
    </button>

    <div v-if="isAuth && showChoices" class="skill-buttons">
      <button
        v-for="option in options"
        :key="option.dots"
        type="button"
        class="skill-option"
        :class="{ active: activeDots === option.dots }"
        @click="selectSkillLevel(option)"
      >
        <span class="skill-option__label">{{ option.label }}</span>
        <span class="skill-option__dots" aria-hidden="true">
          <span v-for="d in option.dots" :key="d" :class="['dot', { filled: activeDots === option.dots }]" />
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { localize } from '@/core/gambits';
import PlayerHelper from '@/core/helpers/player.helper';
import { useRatingStore } from '@/stores/rating.store';
import GamSimpleInput from '@/views/components/GamSimpleInput.vue';
import { GamInputName, GamInputValueType } from '@/views/composables/constants/components/gamInput.constants';
import type { GamInputType } from '@/views/composables/models/components/GamInput';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

const props = defineProps<{ rating?: { isAuth?: boolean } }>();

const isAuth = computed(() => props.rating?.isAuth ?? false);
const showChoices = ref(false);

const ratingStore = useRatingStore();
const { ratings, maxRating } = storeToRefs(ratingStore);

const options = [
  { label: 'Rookie', dots: 1, value: 150 },
  { label: 'Vibing', dots: 2, value: 800 },
  { label: 'Wizard', dots: 3, value: 1500 },
] as const;

const activeDots = computed(() => {
  const r = Number(ratings.value.rating);
  if (!r || r <= 0) return undefined;
  return PlayerHelper.ratingToDots(r);
});

const onLocalRatingChange = (val: unknown) => {
  ratings.value.rating = val as number | undefined;
  if (val !== undefined && val !== null) {
    ratingStore.setRatingDisplayMode(RatingDisplayMode.NUMBER);
    showChoices.value = false;
  }
};

const selectSkillLevel = (option: (typeof options)[number]) => {
  ratings.value.rating = option.value;
  ratingStore.setRatingDisplayMode(RatingDisplayMode.DOTS);
};

const getRatingInput = (): GamInputType => {
  return {
    name: GamInputName.RATING_OFFLINE,
    type: GamInputValueType.NUMBER,
    placeholder: localize('app.component.ratings.item.offline.input'),
    label: localize('app.component.ratings.item.offline.input'),
    value: ratings.value.rating,
    min: 0,
    max: maxRating.value,
  };
};
</script>

<style lang="scss">
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-rating-offline {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding-top: var(--spacing-md);
  width: 100%;
}

.dont-know-cta {
  background: none;
  border: none;
  color: var(--color-white-40);
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  padding: var(--spacing-sm) 0;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 120ms ease;
  align-self: flex-start;

  @include hover {
    color: var(--color-white);
  }

  @include press;
}

.skill-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: var(--spacing-md);
}

.skill-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  width: 100%;
  border: 1px solid var(--color-white-10);
  border-radius: 10px;
  background: transparent;
  color: var(--color-white);
  padding: 12px 14px;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background-color 120ms ease;

  @include hover {
    border-color: var(--color-white-30);
    background: rgb(255 255 255 / 4%);
  }

  &.active {
    border-color: var(--color-primary, var(--color-white));
    background: rgb(255 255 255 / 8%);
  }
}

.skill-option__label {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
}

.skill-option__dots {
  display: flex;
  gap: 5px;
  align-items: center;
}

.gam-rating-offline .dot {
  width: 8px;
  height: 8px;
  border-radius: 1px;
  border: 1.5px solid var(--color-white-50);
  background: transparent;
  transition:
    background-color 120ms ease,
    border-color 120ms ease;

  &.filled {
    background: var(--color-white);
    border-color: var(--color-white);
  }
}
</style>
